import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useCurrencyPickerCodes } from '@/features/fx/api';
import { useBaseCurrency } from '@/lib/session';

/**
 * PER-DOCUMENT CURRENCY (owner decision 2026-10-30): every sales and
 * purchasing document may pick its own currency, defaulting to the partner's.
 *
 * The chain the editors drew before — `doc?.currency ?? partner?.currency ??
 * base` — stays the DEFAULT; a pick made on screen sits in front of it:
 *
 *   picked  →  the saved document's  →  the partner's  →  the company base.
 *
 * Changing the partner drops the pick, so a new draft follows the new
 * partner's currency. The pick also clears a typed exchange rate (the rate
 * belonged to the old currency — the server clears its override the same
 * way). Only an explicit pick is sent (`currency` in the create / patch
 * body); otherwise the server keeps its own default.
 */
export interface DocCurrency {
  /** The currency the screen draws in. */
  currency: string;
  /** The reader's explicit pick ('' = none — the default chain stands). */
  picked: string;
  pick: (code: string) => void;
  /** `{ currency }` when the reader picked one, else nothing. */
  body: () => { currency?: string };
}

export function useDocCurrency({
  partnerId,
  savedCurrency,
  partnerCurrency,
  onPick,
  initialPick,
}: {
  /**
   * A pick carried in from elsewhere — "Record payment" on a EUR invoice
   * opens the receipt in EUR (`?currency=`), so the carried invoice is in
   * the voucher's open list. Dropped like any pick when the partner changes.
   */
  initialPick?: string | null | undefined;
  partnerId: string;
  savedCurrency: string | null | undefined;
  partnerCurrency: string | null | undefined;
  /** Called with every pick — the editors clear their typed rate here. */
  onPick?: ((code: string) => void) | undefined;
}): DocCurrency {
  const base = useBaseCurrency();
  const [picked, setPicked] = useState(() =>
    initialPick && /^[A-Z]{3}$/.test(initialPick) ? initialPick : '',
  );
  const previousPartner = useRef(partnerId);
  useEffect(() => {
    if (previousPartner.current !== partnerId) {
      previousPartner.current = partnerId;
      setPicked('');
    }
  }, [partnerId]);
  // A save re-adopts the stored document; once it holds the pick, the pick is spent.
  useEffect(() => {
    if (picked !== '' && savedCurrency === picked) setPicked('');
  }, [savedCurrency, picked]);

  const currency = picked || savedCurrency || partnerCurrency || base;
  return {
    currency,
    picked,
    pick: (code) => {
      setPicked(code);
      onPick?.(code);
    },
    body: () => (picked === '' ? {} : { currency: picked }),
  };
}

/**
 * The dropdown as a header grid cell, for the editors that do not mount the
 * shared sales header (purchase order, direct purchase return, standalone
 * credit note).
 */
export function DocCurrencyCell({
  idPrefix,
  value,
  onChange,
  disabled,
}: {
  idPrefix: string;
  value: string;
  onChange: (code: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={`${idPrefix}-currency`}>{t('docHeader.currency')}</Label>
      <DocCurrencySelect
        id={`${idPrefix}-currency`}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="h-12 font-mono"
      />
    </div>
  );
}

/**
 * The currency dropdown: the company's offerable currencies (base first, the
 * active rows, and the held code appended when it is neither — BR-MD-01-07's
 * offer-versus-display line, `useCurrencyPickerCodes`).
 */
export function DocCurrencySelect({
  id,
  value,
  onChange,
  disabled,
  className,
}: {
  id: string;
  value: string;
  onChange: (code: string) => void;
  disabled: boolean;
  className?: string | undefined;
}) {
  const { t } = useTranslation();
  const codes = useCurrencyPickerCodes(value);
  return (
    <NativeSelect
      id={id}
      aria-label={t('docHeader.currency')}
      dir="ltr"
      className={className ?? 'h-9 font-mono text-sm'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
    >
      {codes.map((code) => (
        <option key={code} value={code}>
          {code}
        </option>
      ))}
    </NativeSelect>
  );
}
